import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import classes from "./Messages.module.css";
import useMessagesSubscription from '../../hooks/messages/useMessagesSubscription';
import useCreateMessage from '../../hooks/messages/useCreateMessage';
import { useMarkChatAsRead } from '../../hooks/messages/useMessaging';
import { syncBookingFromMessages, parseBookingPreferences } from '../../util/messageBookingParser';
import { ERROR_KIND } from '../../util/classifyError';
import { getNotifier } from '../../util/notifyUser';
import { useQuery } from '@apollo/client';
import { BOOKING_BY_ID } from '../../graphql/queries/bookingQueries';
import { formatBerlinDate, formatBerlinDateTimeRange, formatBerlinTime, isCompletedBookingStatus } from '../../util/bookingDateTime';
import { markConversationAsViewed } from '../../util/messageViewedTracker';
import { getExactLinkedBookingId } from '../../util/bookingLinking';

import fakeProfile from "../../public/fakeUser/noUser.webp"

const SUPPORT_LOGO_SRC = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/stu-logo.svg`;

export const Messages = ({
        activeConversation, 
        isLoading, 
        refetchChatRooms,
        isStudioWindowOpen, 
        setIsStudioWindowOpen, 
        displayMessagesPhone, 
        setPhoneActiveConversation, 
        setDisplayMessagesPhone,
        profilePicture,
        // hasStudios
    }) => {    

    // const hasStudios = false;

    const [newMessage, setNewMessage] = useState("");
    const messagesEndRef = useRef(null);
    const messagesScrollRef = useRef(null);
    const prependScrollRestoreRef = useRef(null);
    const prevLastMessageIdRef = useRef(null);
    const textareaRef = useRef(null);
    const navigate = useNavigate();
    const userId = localStorage.getItem('userId');
    const prevMessagesCountRef = useRef(0);
    const [optimisticMessages, setOptimisticMessages] = useState([]);
    
    const {
        messages,
        loading: messagesLoading,
        loadOlderMessages,
        hasMoreOlder,
        loadingOlder,
        upsertMessage,
    } = useMessagesSubscription(activeConversation?.id);
    const { sendMessage, sendingMessage } = useCreateMessage();
    const { execute: markAsRead } = useMarkChatAsRead();

    // Determine if user is creator (host) or entdecker (guest)
    const isCreator = activeConversation?.hostID === userId;
    const isEntdecker = activeConversation?.guestID === userId;

    // Support chat: no studio, no booking details, no studio-window toggle
    const isSupportChat = activeConversation?.chatType === 'SUPPORT';
    const showSupportBranding = isSupportChat && activeConversation?.guestID === userId;

    // Fetch booking data only when the conversation carries an exact booking id.
    const exactLinkedBookingId = getExactLinkedBookingId(activeConversation);
    const { data: bookingData } = useQuery(BOOKING_BY_ID, {
        variables: { id: exactLinkedBookingId },
        skip: !exactLinkedBookingId,
        fetchPolicy: 'cache-and-network'
    });
    const booking = bookingData?.getBooking;
    
    const adjustTextareaHeight = () => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto';
            const newHeight = Math.min(textarea.scrollHeight, 115);
            textarea.style.height = `${newHeight}px`;
        }
    };
    
    const focusMessageInput = useCallback(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        textarea.focus();
        const cursorPosition = textarea.value.length;
        textarea.setSelectionRange(cursorPosition, cursorPosition);
    }, []);

    const handleMessageChange = (e) => {
        setNewMessage(e.target.value);
        adjustTextareaHeight();
    };

    const handleKeyDown = (e) => {
        // Send message on Enter (without Shift)
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage(e);
        }
        // Shift+Enter creates a new line (default behavior)
    };

    const deliverMessage = useCallback(async (messageContent) => {
        const result = await sendMessage({
            content: messageContent,
            chatRoomId: activeConversation.id,
            senderID: userId,
            chatRoom: activeConversation,
        });
        const createdMessage = result?.data?.createMessage;
        if (createdMessage) {
            upsertMessage(createdMessage);
        }

        if (refetchChatRooms) {
            setTimeout(() => refetchChatRooms(), 120);
        }
        return createdMessage;
    }, [activeConversation, refetchChatRooms, sendMessage, upsertMessage, userId]);

    const handleSendFailure = useCallback((error, tempId) => {
        const displayMessage = error?.displayMessage
            || 'Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.';
        const isBlockedOrValidation = error?.errorKind === ERROR_KIND.BLOCKED
            || error?.errorKind === ERROR_KIND.VALIDATION;

        if (isBlockedOrValidation) {
            setOptimisticMessages((prev) => prev.filter((msg) => msg.id !== tempId));
            getNotifier().toast({
                type: 'info',
                title: 'Nachricht nicht gesendet',
                message: displayMessage,
                duration: 6000,
            });
            return;
        }

        setOptimisticMessages((prev) => prev.map((msg) => (
            msg.id === tempId
                ? { ...msg, sendFailed: true, isSending: false }
                : msg
        )));
    }, []);

    const dismissFailedMessage = useCallback((messageId) => {
        setOptimisticMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    }, []);

    const retryFailedMessage = useCallback(async (failedMessage) => {
        if (!failedMessage?.content || failedMessage.isSending || sendingMessage) return;

        setOptimisticMessages((prev) => prev.map((msg) => (
            msg.id === failedMessage.id
                ? { ...msg, sendFailed: false, isSending: true }
                : msg
        )));

        if (refetchChatRooms) {
            refetchChatRooms();
        }

        try {
            await deliverMessage(failedMessage.content);
            setOptimisticMessages((prev) => prev.filter((msg) => msg.id !== failedMessage.id));
        } catch (error) {
            handleSendFailure(error, failedMessage.id);
        }
    }, [deliverMessage, handleSendFailure, refetchChatRooms, sendingMessage]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;
        
        // Check if chat is read-only (archived inquiry chat)
        if (activeConversation?.chatStatus === 'READ_ONLY' && activeConversation?.chatType === 'INQUIRY') {
            return;
        }
        
        const messageContent = newMessage.trim();
        const tempId = `optimistic-${Date.now()}-${Math.random()}`;
        
        const optimisticMessage = {
            id: tempId,
            content: messageContent,
            senderID: userId,
            chatRoomID: activeConversation.id,
            createdAt: new Date().toISOString(),
            type: 'TEXT',
            isOptimistic: true,
            sendFailed: false,
            isSending: true,
        };
        
        setOptimisticMessages(prev => [...prev, optimisticMessage]);
        setNewMessage("");
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
        requestAnimationFrame(() => {
            focusMessageInput();
        });
        
        setTimeout(() => {
            scrollToBottom();
        }, 0);

        if (refetchChatRooms) {
            refetchChatRooms();
        }
        
        try {
            await deliverMessage(messageContent);
            setOptimisticMessages((prev) => prev.filter((msg) => msg.id !== tempId));
        } catch (error) {
            handleSendFailure(error, tempId);
            requestAnimationFrame(() => {
                focusMessageInput();
            });
        }
    };
    
    const scrollToBottom = (behavior = "smooth") => {
        messagesEndRef.current?.scrollIntoView({ behavior });
    };

    // Combine real messages with optimistic messages, but exclude optimistic messages that have matching real messages
    // This prevents the glitch by keeping optimistic messages until real ones arrive
    const allMessages = useMemo(() => {
        // Filter out optimistic messages that have a matching real message
        const filteredOptimistic = optimisticMessages.filter(optMsg => {
            const timeWindow = 15000; // 15 seconds window for matching
            const optTime = new Date(optMsg.createdAt).getTime();
            const optContent = (optMsg.content || '').trim();
            
            // Check if there's a real message that matches this optimistic one
            for (const realMsg of messages) {
                if (realMsg.isOptimistic) continue;
                
                const realTime = new Date(realMsg.createdAt).getTime();
                const timeDiff = Math.abs(realTime - optTime);
                const realContent = (realMsg.content || '').trim();
                
                // Match if same content (trimmed), same sender, and within time window
                // Use a more lenient time window to account for network delays
                if (realContent === optContent &&
                    realMsg.senderID === optMsg.senderID &&
                    timeDiff < timeWindow) {
                    return false; // Exclude this optimistic message, real one exists
                }
            }
            return true; // Keep this optimistic message - no matching real message yet
        });
        
        // Combine and sort by timestamp
        return [...messages, ...filteredOptimistic].sort((a, b) => {
            const dateA = new Date(a.createdAt).getTime();
            const dateB = new Date(b.createdAt).getTime();
            return dateA - dateB;
        });
    }, [messages, optimisticMessages]);

    // Keep showing a loader when conversation metadata indicates there should
    // be messages, but the message list has not arrived yet.
    const hasExpectedMessagesFromConversation = Boolean(
        activeConversation?.lastMessageAt ||
        (activeConversation?.lastMessage?.content && activeConversation.lastMessage.content.trim() !== '')
    );
    // Support chats can carry a lastMessageAt without any fetchable messages,
    // which would otherwise leave the spinner up forever — skip the heuristic for them.
    const shouldShowMessagesLoading =
        messagesLoading || (!isSupportChat && hasExpectedMessagesFromConversation && allMessages.length === 0);

    const LOAD_OLDER_SCROLL_THRESHOLD_PX = 96;

    const handleMessagesScroll = useCallback(() => {
        const el = messagesScrollRef.current;
        if (!el || loadingOlder || !hasMoreOlder) return;
        if (el.scrollTop > LOAD_OLDER_SCROLL_THRESHOLD_PX) return;

        prependScrollRestoreRef.current = {
            height: el.scrollHeight,
            top: el.scrollTop,
        };
        loadOlderMessages();
    }, [loadingOlder, hasMoreOlder, loadOlderMessages]);

    useLayoutEffect(() => {
        if (loadingOlder) return;
        const payload = prependScrollRestoreRef.current;
        if (!payload || !messagesScrollRef.current) return;
        const el = messagesScrollRef.current;
        const delta = el.scrollHeight - payload.height;
        if (delta > 0) {
            el.scrollTop = payload.top + delta;
        }
        prependScrollRestoreRef.current = null;
    }, [loadingOlder, allMessages]);

    useEffect(() => {
        prevLastMessageIdRef.current = null;
        setNewMessage("");
        setOptimisticMessages([]);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }, [activeConversation?.id]);

    // Ensure newly opened conversations land at the latest message.
    useEffect(() => {
        if (!activeConversation?.id || messagesLoading) return;
        requestAnimationFrame(() => scrollToBottom("auto"));
        const timer = setTimeout(() => scrollToBottom("auto"), 120);
        return () => clearTimeout(timer);
    }, [activeConversation?.id, messagesLoading, allMessages.length]);

    // Clear draft immediately when phone messages view is closed
    useEffect(() => {
        if (!displayMessagesPhone) {
            setNewMessage("");
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        }
    }, [displayMessagesPhone]);

    // Scroll to bottom when a new message arrives at the end — not when prepending older history.
    useEffect(() => {
        if (!allMessages.length) {
            prevLastMessageIdRef.current = null;
            return;
        }
        const lastId = allMessages[allMessages.length - 1].id;
        if (lastId !== prevLastMessageIdRef.current) {
            scrollToBottom();
        }
        prevLastMessageIdRef.current = lastId;
    }, [allMessages]);

    // Mark chat as read when component mounts or chat room changes
    useEffect(() => {
        if (activeConversation?.id) {
            markAsRead(activeConversation.id);
            // Also mark conversation as viewed in our tracker
            const isHost = activeConversation.hostID === userId;
            const unreadCount = isHost 
                ? (activeConversation.hostUnreadCount || 0)
                : (activeConversation.guestUnreadCount || 0);
            markConversationAsViewed(activeConversation.id, unreadCount, userId);
        }
    }, [activeConversation?.id, markAsRead, activeConversation?.hostID, activeConversation?.guestID, activeConversation?.hostUnreadCount, activeConversation?.guestUnreadCount, userId]);

    // Mark chat as read when new messages arrive (if not from current user)
    // Only check real messages, not optimistic ones
    useEffect(() => {
        if (messages.length > prevMessagesCountRef.current && activeConversation?.id) {
            const lastMessage = messages[messages.length - 1];
            if (lastMessage && lastMessage.senderID !== userId && !lastMessage.isOptimistic) {
                markAsRead(activeConversation.id);
            }
        }
        prevMessagesCountRef.current = messages.length;
    }, [messages.length, activeConversation?.id, markAsRead, userId, messages]);

    // Clean up stale in-flight optimistic messages; keep failed ones until retry/dismiss.
    useEffect(() => {
        if (optimisticMessages.length > 0) {
            const now = Date.now();
            setOptimisticMessages(prev => {
                return prev.filter(optMsg => {
                    if (optMsg.sendFailed) return true;
                    const msgAge = now - new Date(optMsg.createdAt).getTime();
                    return msgAge < 30000;
                });
            });
        }
    }, [messages, optimisticMessages.length]);

    // Sync booking preferences from messages to localStorage for the current studio
    // Only sync real messages, not optimistic ones
    useEffect(() => {
        const studioId = activeConversation?.studio?.id;
        if (!studioId || !messages || messages.length === 0) return;
        try {
            syncBookingFromMessages(studioId, messages);
        } catch (e) {
            // fail silently to avoid disrupting chat UI
        }
    }, [messages, activeConversation?.studio?.id]);

    // Add an effect to handle browser back button
    useEffect(() => {
        const handlePopState = () => {
            if (window.innerWidth <= 768 && displayMessagesPhone) {
                setPhoneActiveConversation(null);
                setDisplayMessagesPhone(false);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [displayMessagesPhone, setPhoneActiveConversation, setDisplayMessagesPhone]);


    const formatMessageTime = (timestamp) => {
        if (!timestamp) return '';
        return formatBerlinTime(timestamp, {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatConversationStartDate = (createdAt) => {
        if (!createdAt) return '';
        const date = new Date(createdAt);
        
        // Abbreviated day names in German
        const dayNames = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];
        const dayName = dayNames[date.getDay()];
        
        // Abbreviated month names in German
        const monthNames = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];
        const monthName = monthNames[date.getMonth()];
        
        const day = date.getDate();
        
        return `${dayName} ${day}. ${monthName}`;
    };

    // Format inquiry date - show "Heute" if today, otherwise formatted date
    const formatInquiryDate = (createdAt) => {
        if (!createdAt) return '';
        const date = new Date(createdAt);
        const today = new Date();
        
        // Check if it's today (same day, month, year)
        const isToday = date.getDate() === today.getDate() &&
                       date.getMonth() === today.getMonth() &&
                       date.getFullYear() === today.getFullYear();
        
        if (isToday) {
            return 'Heute';
        }
        
        // Otherwise format as "Mo. 22. Jan."
        const dayNames = ['So.', 'Mo.', 'Di.', 'Mi.', 'Do.', 'Fr.', 'Sa.'];
        const dayName = dayNames[date.getDay()];
        const monthNames = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];
        const monthName = monthNames[date.getMonth()];
        const day = date.getDate();
        
        return `${dayName} ${day}. ${monthName}`;
    };

    const isSameCalendarDay = (a, b) => {
        if (!a || !b) return false;
        const first = new Date(a);
        const second = new Date(b);
        return (
            first.getFullYear() === second.getFullYear() &&
            first.getMonth() === second.getMonth() &&
            first.getDate() === second.getDate()
        );
    };

    const renderDateSeparator = (message, index, sourceMessages) => {
        if (!message?.createdAt) return null;
        const prevMessage = index > 0 ? sourceMessages[index - 1] : null;
        if (prevMessage?.createdAt && isSameCalendarDay(prevMessage.createdAt, message.createdAt)) {
            return null;
        }

        return (
            <div className={classes.conversationStartDate}>
                <span className="regularXxs">
                    {formatInquiryDate(message.createdAt)}
                </span>
            </div>
        );
    };

    // const formatStatusDate = (createdAt) => {
    //     if (!createdAt) return '';
    //     const date = new Date(createdAt);
        
    //     // Validate date
    //     if (isNaN(date.getTime())) return '';
        
    //     // Abbreviated month names in German
    //     const monthNames = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];
    //     const monthName = monthNames[date.getMonth()];
        
    //     const day = date.getDate();
    //     const year = date.getFullYear();
        
    //     return `${day}. ${monthName} ${year}`;
    // };

    const formatBookingDateTime = (startDateTime, endDateTime) => {
        return formatBerlinDateTimeRange(startDateTime, endDateTime, {
            dateOptions: {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
            },
            sameDaySeparator: ' - ',
            timeSeparator: '–',
        });
    };

    // Format date/time from parsed message booking preferences
    const formatParsedBookingDateTime = (parsed) => {
        if (!parsed || !parsed.dateISO) return null;

        try {
            const date = new Date(parsed.dateISO + 'T00:00:00');
            if (isNaN(date.getTime())) return null;

            // Format date as "19. März 2026" (day. month. year)
            const day = date.getDate();
            const monthNames = ['Jan.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sept.', 'Okt.', 'Nov.', 'Dez.'];
            const month = monthNames[date.getMonth()];
            const year = date.getFullYear();
            const dateStr = `${day}. ${month} ${year}`;

            // Format time if available
            let timeStr = null;
            if (parsed.startTime && parsed.endTime) {
                timeStr = `${parsed.startTime}–${parsed.endTime} Uhr`;
            }

            return {
                date: dateStr,
                time: timeStr
            };
        } catch (error) {
            return null;
        }
    };

    // Clean message content by removing booking info lines
    const cleanMessageContent = (content) => {
        if (!content || typeof content !== 'string') return content;
        
        // Remove lines with booking date/time info
        const lines = content.split('\n');
        const cleanedLines = lines.filter(line => {
            const trimmed = line.trim();
            // Remove lines with date emoji and pattern
            if (trimmed.match(/^📅\s*Gewünschtes\s*Datum:/)) return false;
            // Remove lines with time emoji and pattern
            if (trimmed.match(/^⏰\s*(?:Zeit|Bevorzugte Zeit):/)) return false;
            if (trimmed.match(/^⏰\s*(?:Ich bin zeitlich flexibel|Alternativ bin ich auch zeitlich flexibel)/i)) return false;
            return true;
        });
        
        // Join back and clean up multiple consecutive newlines
        return cleanedLines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
    };

    const calculateHoursRemaining = (createdAt) => {
        if (!createdAt) return null;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        const hoursRemaining = Math.max(0, Math.ceil(24 - hoursPassed));
        return hoursRemaining;
    };

    const isBookingExpired = (createdAt) => {
        if (!createdAt) return false;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        return hoursPassed > 24;
    };

    // const getStatusMessage = () => {
    //     // Only show status if there's booking data or if conversation is booking-related
    //     // Also check if we found a booking via fallback query
    //     if (!booking && !activeConversation?.isBookingRelated && !activeConversation?.linkedBookingID && !activeConversation?.bookingStartDate) {
    //         return null;
    //     }

    //     const status = booking?.status;
    //     const hostName = booking?.Studio?.User?.firstName || activeConversation?.host?.firstName || '';
    //     const guestName = booking?.User?.firstName || activeConversation?.guest?.firstName || '';
    //     const studioName = booking?.Studio?.StudioName || activeConversation?.studio?.StudioName || '';
    //     const cancelledBy = booking?.cancelledBy;
    //     const createdAt = booking?.createdAt || activeConversation?.createdAt;
    //     const expired = isBookingExpired(createdAt);
    //     const completed = isBookingCompleted();
    //     const dateTime = formatBookingDateTime(booking?.startDateTime || activeConversation?.bookingStartDate, booking?.endDateTime || activeConversation?.bookingEndDate);

    //     // Check if cancelled by guest or host
    //     // cancelledBy is an enum: "HOST" | "GUEST" | "SYSTEM"
    //     const cancelledByGuest = cancelledBy === "GUEST";
    //     const cancelledByHost = cancelledBy === "HOST";
    //     const cancelledBySystem = cancelledBy === "SYSTEM";

    //     // Check if booking is flexible (time is flexible)
    //     const isFlexible = booking?.isFlexible || false;

    //     // Handle COMPLETED status first
    //     if (status === "COMPLETED" || completed) {
    //         return {
    //             text: 'Die Buchung ist abgeschlossen.',
    //             actionText: isCreator ? 'Gast bewerten' : 'Buchung bewerten',
    //             hasAction: true
    //         };
    //     }

    //     // Handle NO_SHOW status
    //     if (status === "NO_SHOW") {
    //         return {
    //             text: isCreator 
    //                 ? `${guestName} ist nicht erschienen`
    //                 : 'Du bist nicht erschienen',
    //             hasAction: false
    //         };
    //     }

    //     // Handle EXPIRED status
    //     if (status === "EXPIRED" || (expired && status === "PENDING")) {
    //         return {
    //             text: 'Die Anfrage ist abgelaufen.',
    //             hasAction: false
    //         };
    //     }

    //     // Handle INQUIRY status (can have dateTime or not)
    //     if (status === "INQUIRY" || (status === "PENDING" && !expired && !dateTime)) {
    //         if (isCreator) {
    //             if (dateTime) {
    //                 return {
    //                     text: `Erkundigung von ${guestName} erhalten: ${studioName} ${dateTime.date}, ${dateTime.time}${isFlexible ? ' (zeitlich flexibel)' : ''}`,
    //                     hasAction: false
    //                 };
    //             } else {
    //                 return {
    //                     text: `Erkundigung von ${guestName} erhalten: ${studioName}`,
    //                     hasAction: false
    //                 };
    //             }
    //         } else {
    //             if (dateTime) {
    //                 return {
    //                     text: `Erkundigung gesendet: ${studioName} ${dateTime.date}, ${dateTime.time}${isFlexible ? ' (zeitlich flexibel)' : ''}`,
    //                     hasAction: false
    //                 };
    //             } else {
    //                 return {
    //                     text: `Erkundigung gesendet: ${studioName}`,
    //                     hasAction: false
    //                 };
    //             }
    //         }
    //     }

    //     // Handle PENDING with specific date/time (Anfrage)
    //     if (status === "PENDING" && !expired && dateTime) {
    //         if (isCreator) {
    //             return {
    //                 text: `Anfrage von ${guestName} erhalten: ${studioName} ${dateTime.date}, ${dateTime.time}`,
    //                 hasAction: false
    //             };
    //         } else {
    //             return {
    //                 text: `Du hast eine Anfrage gesendet: ${studioName} ${dateTime.date}, ${dateTime.time}`,
    //                 hasAction: false
    //             };
    //         }
    //     }

    //     if (status === "WITHDRAWN") {
    //         return {
    //             text: isCreator 
    //                 ? `${guestName} hat die Anfrage zurückgezogen`
    //                 : 'Du hast die Anfrage zurückgezogen',
    //             hasAction: false
    //         };
    //     }

    //     if (status === "DECLINED" || status === "REJECTED") {
    //         return {
    //             text: isCreator 
    //                 ? 'Du hast die Buchungsanfrage abgelehnt'
    //                 : `${hostName} hat die Buchungsanfrage abgelehnt`,
    //             hasAction: false
    //         };
    //     }

    //     if (status === "CONFIRMED") {
    //         return {
    //             text: isCreator 
    //                 ? 'Du hast die Buchung bestätigt'
    //                 : `${hostName} hat die Buchung bestätigt`,
    //             hasAction: false
    //         };
    //     }

    //     if (status === "CANCELLED") {
    //         if (isCreator) {
    //             if (cancelledByGuest) {
    //                 return {
    //                     text: `${guestName} hat die Buchung storniert`,
    //                     hasAction: false
    //                 };
    //             } else if (cancelledByHost) {
    //                 return {
    //                     text: 'Du hast die Buchung storniert',
    //                     hasAction: false
    //                 };
    //             } else if (cancelledBySystem) {
    //                 return {
    //                     text: 'Die Buchung wurde automatisch storniert',
    //                     hasAction: false
    //                 };
    //             } else {
    //                 return {
    //                     text: 'Die Buchung wurde storniert',
    //                     hasAction: false
    //                 };
    //             }
    //         } else {
    //             if (cancelledByHost) {
    //                 return {
    //                     text: `${hostName} hat die Buchung storniert`,
    //                     hasAction: false
    //                 };
    //             } else if (cancelledByGuest) {
    //                 return {
    //                     text: 'Du hast die Buchung storniert',
    //                     hasAction: false
    //                 };
    //             } else if (cancelledBySystem) {
    //                 return {
    //                     text: 'Die Buchung wurde automatisch storniert',
    //                     hasAction: false
    //                 };
    //             } else {
    //                 return {
    //                     text: 'Die Buchung wurde storniert',
    //                     hasAction: false
    //                 };
    //             }
    //         }
    //     }
        
    //     return null;
    // };

    // Helper function to determine if a message should show the profile image
    
    const shouldShowProfileImage = (currentIndex, messages) => {
        const currentMessage = messages[currentIndex];
        const nextMessage = messages[currentIndex + 1];
        
        // Show profile image if:
        // 1. This is the last message overall, OR
        // 2. The next message is from a different sender
        return !nextMessage || nextMessage.senderID !== currentMessage.senderID;
    };

    // Helper function to determine if next message is from the same user
    const isNextMessageFromSameUser = (currentIndex, messages) => {
        const nextMessage = messages[currentIndex + 1];
        const currentMessage = messages[currentIndex];
        
        return nextMessage && nextMessage.senderID === currentMessage.senderID;
    };

    // Check if message type is a booking status message
    const isBookingStatusMessage = (messageType) => {
        const bookingStatusTypes = [
            'BOOKING_CREATED',
            'BOOKING_CONFIRMED',
            'BOOKING_DECLINED',
            'BOOKING_CANCELLED',
            'BOOKING_WITHDRAWN',
            'BOOKING_EXPIRED',
            'BOOKING_COMPLETED',
            'BOOKING_NO_SHOW',
            'INQUIRY_REQUEST',
            'INQUIRY_CONVERTED'
        ];
        return bookingStatusTypes.includes(messageType);
    };

    // Get effective cancelledBy with fallback logic
    const getEffectiveCancelledBy = (message, bookingData) => {
        // If not BOOKING_CANCELLED, return bookingData.cancelledBy
        if (message.type !== 'BOOKING_CANCELLED') {
            return bookingData?.cancelledBy;
        }

        // If cancelledBy is set, use it
        if (bookingData?.cancelledBy) {
            return bookingData.cancelledBy;
        }

        // Fallback: derive from currentUserId vs booking owner/guest
        if (userId && bookingData) {
            const guestUserId = bookingData.userID || bookingData.guestID;
            if (userId === guestUserId) return 'GUEST';
            if (userId === bookingData.hostID || userId === bookingData.studioOwnerID) return 'HOST';
        }

        // Last fallback: based on role
        return isCreator ? 'GUEST' : 'HOST';
    };

    // Generate status message text based on message type, cancelledBy, and role
    const getBookingStatusMessageText = (message, bookingData) => {
        if (!message.type || !isBookingStatusMessage(message.type)) {
            return null;
        }

        const messageType = message.type;
        const effectiveCancelledBy = getEffectiveCancelledBy(message, bookingData);
        const hostName = bookingData?.Studio?.User?.firstName || activeConversation?.host?.firstName || 'Host';
        const guestName = bookingData?.User?.firstName || activeConversation?.guest?.firstName || 'Gast';
        const studioName = bookingData?.Studio?.StudioName || activeConversation?.studio?.StudioName || '';
        const dateTime = formatBookingDateTime(bookingData?.startDateTime, bookingData?.endDateTime);

        switch (messageType) {
            case 'BOOKING_CREATED':
                // This should show a booking card, not a status message
                // For now, return null - we'll handle this separately
                return null;

            case 'BOOKING_CONFIRMED':
                return isCreator
                    ? `Du hast die Buchung von ${guestName} bestätigt`
                    : `${hostName} hat deine Buchung bestätigt`;

            case 'BOOKING_DECLINED':
            case 'REQUEST_DECLINED':
                return isCreator
                    ? 'Du hast die Buchungsanfrage abgelehnt'
                    : `${hostName} hat die Buchungsanfrage abgelehnt`;

            case 'BOOKING_CANCELLED':
                if (effectiveCancelledBy === 'HOST') {
                    return isCreator
                        ? 'Du hast die Buchung storniert'
                        : `${hostName} hat die Buchung storniert`;
                } else if (effectiveCancelledBy === 'GUEST') {
                    return isCreator
                        ? `${guestName} hat die Buchung storniert`
                        : 'Du hast die Buchung storniert';
                } else if (effectiveCancelledBy === 'SYSTEM') {
                    return 'Die Buchung wurde automatisch storniert';
                } else {
                    // Fallback
                    return isCreator
                        ? `${guestName} hat die Buchung storniert`
                        : `${hostName} hat die Buchung storniert`;
                }

            case 'BOOKING_WITHDRAWN':
                return isCreator
                    ? `${guestName} hat die Anfrage zurückgezogen`
                    : 'Du hast die Anfrage zurückgezogen';

            case 'BOOKING_EXPIRED':
                return 'Die Anfrage ist abgelaufen.';

            case 'BOOKING_COMPLETED':
                return 'Die Buchung ist abgeschlossen.';

            case 'BOOKING_NO_SHOW':
                return isCreator
                    ? `${guestName} ist nicht erschienen`
                    : 'Du bist nicht erschienen';

            case 'INQUIRY_REQUEST':
                if (dateTime) {
                    return isCreator
                        ? `Erkundigung von ${guestName} erhalten: ${studioName} ${dateTime.date}, ${dateTime.time}`
                        : `Erkundigung gesendet: ${studioName} ${dateTime.date}, ${dateTime.time}`;
                } else {
                    return isCreator
                        ? `Erkundigung von ${guestName} erhalten: ${studioName}`
                        : `Erkundigung gesendet: ${studioName}`;
                }

            default:
                return null;
        }
    };

    // Format date and time for system messages
    const formatSystemMessageDateTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const dateStr = formatBerlinDate(date, {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit'
        });
        const timeStr = formatBerlinTime(date, {
            hour: '2-digit',
            minute: '2-digit'
        });
        return `${dateStr}, ${timeStr}`;
    };

    // Render a booking status message (centered, gray text)
    const renderBookingStatusMessage = (message, bookingData) => {
        const statusText = getBookingStatusMessageText(message, bookingData);
        if (!statusText) return null;

        // Only show date/time for CANCELLED and EXPIRED messages
        const messageType = message.type;
        const showDateTime = messageType === 'BOOKING_CANCELLED' || messageType === 'BOOKING_EXPIRED';

        return (
            <div className={classes.systemMessageContainer}>
                <p className={`${classes.systemMessageText} regularXxs`}>
                    {statusText}
                </p>
                {showDateTime && message.createdAt && (
                    <span className={`${classes.systemMessageTime} semiboldXxs`}>
                        {formatSystemMessageDateTime(message.createdAt)}
                    </span>
                )}
                {!showDateTime && message.createdAt && (
                    <span className={`${classes.systemMessageTime} semiboldXxs`}>
                        {formatMessageTime(message.createdAt)}
                    </span>
                )}
            </div>
        );
    };

    // Get booking card status text with person name
    const getBookingCardStatus = (bookingData) => {
        if (!bookingData) return null;

        const status = bookingData.status;
        const createdAt = bookingData.createdAt || activeConversation?.createdAt;
        const expired = isBookingExpired(createdAt);
        const hoursRemaining = calculateHoursRemaining(createdAt);
        const completed = isCompletedBookingStatus(status);

        if (completed) {
            return {
                text: 'Abgeschlossen',
                color: '#A1A7AE',
                showPerson: false
            };
        }

        if (status === "NO_SHOW") {
            return {
                text: 'Nicht erschienen',
                color: '#CE425D',
                showPerson: false
            };
        }

        if (status === "PENDING" && !expired) {
            if (hoursRemaining !== null && hoursRemaining > 0) {
                const hoursText = hoursRemaining === 1 ? 'Stunde' : 'Stunden';
                return {
                    text: `Angefragt: Läuft in ${hoursRemaining} ${hoursText} ab`,
                    color: '#FF6B35',
                    showPerson: true
                };
            }
            return {
                text: 'Angefragt',
                color: '#FF6B35',
                showPerson: true
            };
        }

        if (status === "DECLINED" || status === "REJECTED") {
            return {
                text: 'Anfrage abgelehnt',
                color: 'var(--stu-rot)',
                showPerson: false
            };
        }

        if (status === "CONFIRMED") {
            return {
                text: 'Buchung bestätigt',
                color: '#20BD13',
                showPerson: false
            };
        }

        if (status === "CANCELLED") {
            return {
                text: 'Storniert',
                color: 'var(--stu-rot)',
                showPerson: false
            };
        }

        if (status === "WITHDRAWN") {
            return {
                text: 'Anfrage zurückgezogen',
                color: 'var(--stu-rot)',
                showPerson: false
            };
        }

        if (status === "EXPIRED" || expired) {
            return {
                text: 'Anfrage abgelaufen',
                color: '#CE425D',
                showPerson: false
            };
        }

        return {
            text: 'Angefragt',
            color: '#FF6B35',
            showPerson: true
        };
    };

    // Get top status message text (displayed before booking card)
    const getTopStatusMessage = () => {
        
        // Show for booking-related conversations OR inquiry chats
        const isBookingRelated = !!booking || !!exactLinkedBookingId;
        const isInquiryChat = activeConversation?.chatType === 'INQUIRY';
        
        if (!isBookingRelated && !isInquiryChat) {
            return null;
        }

        const status = booking?.status;
        const hostName = booking?.Studio?.User?.firstName || activeConversation?.host?.firstName || 'Host';
        // Try to get guest name from multiple sources, including chatPartner
        const guestName = booking?.User?.firstName || 
                         activeConversation?.guest?.firstName || 
                         activeConversation?.chatPartner?.firstName || 
                         'Gast';
        const studioName = booking?.Studio?.StudioName || 
                          (typeof activeConversation?.studio === 'string' ? activeConversation.studio : activeConversation?.studio?.StudioName) || 
                          '';
        
        // Try to get date/time from booking first, then from conversation, then from first message
        let dateTime = formatBookingDateTime(
            booking?.startDateTime || activeConversation?.bookingStartDate, 
            booking?.endDateTime || activeConversation?.bookingEndDate
        );
        
        // If no dateTime from booking/conversation, try to extract from first message
        let parsedFromMessage = null;
        if (!dateTime && isInquiryChat && allMessages && allMessages.length > 0) {
            const firstMessage = allMessages[0];
            parsedFromMessage = parseBookingPreferences(firstMessage?.content || '');
            if (parsedFromMessage) {
                dateTime = formatParsedBookingDateTime(parsedFromMessage);
            }
        }
        
        const isFlexible = booking?.isFlexible || parsedFromMessage?.isFlexible || false;
        const expired = isBookingExpired(booking?.createdAt || activeConversation?.createdAt);
        const completed = isCompletedBookingStatus(status);


        // Entdecker (Guest) messages
        if (isEntdecker) {
            // 1. Erkundigung (INQUIRY) - check status OR chatType
            if (status === "INQUIRY" || isInquiryChat || (status === "PENDING" && !dateTime)) {
                if (dateTime && dateTime.date) {
                    const flexibleText = isFlexible ? ' (zeitlich flexibel)' : '';
                    const timePart = dateTime.time ? ` ${dateTime.time}` : '';
                    return `Erkundigung gesendet: ${studioName} ${dateTime.date}${timePart}${flexibleText}`;
                } else {
                    return `Erkundigung gesendet: ${studioName}`;
                }
            }

            // 2. Anfrage gesendet (PENDING with date/time)
            if (status === "PENDING" && !expired && dateTime) {
                return `Du hast eine Anfrage gesendet: ${studioName} ${dateTime.date}, ${dateTime.time}`;
            }

            // 3. Anfrage zurückgezogen (WITHDRAWN)
            if (status === "WITHDRAWN") {
                return 'Du hast die Anfrage zurückgezogen';
            }

            // 4. Anfrage abgelehnt (DECLINED/REJECTED)
            if (status === "DECLINED" || status === "REJECTED") {
                return `${hostName} hat die Buchungsanfrage abgelehnt`;
            }

            // 5. Buchung bestätigt (CONFIRMED)
            if (status === "CONFIRMED") {
                return `${hostName} hat die Buchung bestätigt`;
            }

            // Note: CANCELLED and EXPIRED are handled in system messages only (not shown here)

            // 6. Abgeschlossen (COMPLETED)
            if (completed) {
                return 'Die Buchung ist abgeschlossen.';
            }
        }

        // Creator (Host) messages
        if (isCreator) {
            // 1. Erkundigung (INQUIRY) - check status OR chatType
            if (status === "INQUIRY" || isInquiryChat || (status === "PENDING" && !dateTime)) {
                if (dateTime && dateTime.date) {
                    const flexibleText = isFlexible ? ' (zeitlich flexibel)' : '';
                    const timePart = dateTime.time ? ` ${dateTime.time}` : '';
                    return `Erkundigung von ${guestName} erhalten: ${studioName} ${dateTime.date}${timePart}${flexibleText}`;
                } else {
                    return `Erkundigung von ${guestName} erhalten: ${studioName}`;
                }
            }

            // 2. Anfrage erhalten (PENDING with date/time)
            if (status === "PENDING" && !expired && dateTime) {
                return `Anfrage von ${guestName} erhalten: ${studioName} ${dateTime.date}, ${dateTime.time}`;
            }

            // 3. Anfrage zurückgezogen (WITHDRAWN)
            if (status === "WITHDRAWN") {
                return `${guestName} hat die Anfrage zurückgezogen`;
            }

            // 4. Buchungsanfrage abgelehnt (DECLINED/REJECTED)
            if (status === "DECLINED" || status === "REJECTED") {
                return 'Du hast die Buchungsanfrage abgelehnt';
            }

            // 5. Buchung bestätigt (CONFIRMED)
            if (status === "CONFIRMED") {
                return 'Du hast die Buchung bestätigt';
            }

            // Note: CANCELLED and EXPIRED are handled in system messages only (not shown here)

            // 6. Abgeschlossen (COMPLETED)
            if (completed) {
                return 'Die Buchung ist abgeschlossen.';
            }
        }

        return null;
    };

    // Render top status message
    const renderTopStatusMessage = () => {
        const statusMessage = getTopStatusMessage();
        if (!statusMessage) return null;

        const bookingId = booking?.id || exactLinkedBookingId;
        const status = booking?.status;
        const isCompleted = isCompletedBookingStatus(status);

        // For completed bookings, add rating link
        if (isCompleted && bookingId) {
            const ratingText = isCreator ? 'Gast bewerten' : 'Buchung bewerten';
            const bookingDetailsPath = isCreator ? `/creator/details/${bookingId}` : `/bookings/details/${bookingId}`;
            return (
                <div className={classes.topStatusMessageContainer}>
                    <div className={classes.topStatusMessage}>
                        <span className="regularXxs">{statusMessage} </span>
                        <Link to={bookingDetailsPath} className={classes.topStatusLink}>
                            <span className="regularXxs">{ratingText}</span>
                        </Link>
                    </div>
                </div>
            );
        }

        return (
            <div className={classes.topStatusMessageContainer}>
                <div className={classes.topStatusMessage}>
                    <span className="regularXxs">{statusMessage}</span>
                </div>
            </div>
        );
    };

    // Render booking card
    const renderBookingCard = () => {
        // Don't show booking card for inquiry-only chats (no actual booking)
        const isInquiryOnly = activeConversation?.chatType === 'INQUIRY' && 
                             !activeConversation?.isBookingRelated && 
                             !exactLinkedBookingId &&
                             !booking;
        
        if (isInquiryOnly) {
            return null;
        }
        
        if (!booking) {
            return null;
        }

        // Use booking data if available, otherwise fall back to conversation data
        // Handle case where studio might be a string or an object
        const studioName = booking?.Studio?.StudioName || 
                          (typeof activeConversation?.studio === 'string' ? activeConversation.studio : activeConversation?.studio?.StudioName) || 
                          '';
        const projectName = booking?.projectName || '';
        const guestName = booking?.User?.firstName || activeConversation?.guest?.firstName || '';
        const dateTime = formatBookingDateTime(
            booking?.startDateTime || activeConversation?.bookingStartDate, 
            booking?.endDateTime || activeConversation?.bookingEndDate
        );
        const price = booking?.totalPrice ? `${booking.totalPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Euro` : '';
        const statusInfo = booking ? getBookingCardStatus(booking) : null;
        const bookingId = booking?.id || exactLinkedBookingId;

        // Show card if we have studio name (minimum requirement)
        if (!studioName) {
            return null;
        }

        return (
            <div className={classes.bookingCardContainer}>
                <div className={classes.bookingCard}>
                    <div className={classes.bookingCardContent}>
                        <div className={classes.bookingCardHeader}>
                            <h3 className={`${classes.bookingCardTitle}  semiboldSmall`}>{studioName}</h3>
                            {projectName && (
                                <p className={`${classes.bookingCardSubtitle} semiboldXs`}>{projectName}</p>
                            )}
                        </div>
                        <div className={classes.horizontalLine}></div>
                        {statusInfo && (
                            <div className={classes.bookingCardStatus}>
                                <span style={{ color: statusInfo.color }} className="regularXs">
                                    {statusInfo.text}
                                </span>
                                {statusInfo.showPerson && guestName && (
                                    <>
                                        <span className={`regularXs ${classes.displayNonePhone}`}> • </span>
                                        <span className="regularXs">{guestName}</span>
                                    </>
                                )}
                            </div>
                        )}
                        {dateTime && (
                            <p className={`${classes.bookingCardDateTime} regularXs`}>
                                {dateTime.date}, {dateTime.time}
                            </p>
                        )}
                        {price && (
                            <p className={`${classes.bookingCardPrice} regularXs`}>{price}</p>
                        )}
                    </div>
                    <div className={classes.bookingCardActions}>
                        {isCreator && booking && booking.status === "PENDING" && !isBookingExpired(booking?.createdAt) && (
                            <Link className={classes.bookingCardPrimaryButtonLink} to={bookingId ? `/creator/details/${bookingId}` : '#'}>
                                <button className={`${classes.bookingCardPrimaryButton} semiboldXs`}>
                                    Bestätigen oder ablehnen
                                </button>
                            </Link>
                        )}
                        {bookingId && (
                            <Link className={classes.bookingCardDetailsButtonLink} to={isCreator ? `/creator/details/${bookingId}` : `/bookings/details/${bookingId}`}>
                                <button className={`${classes.bookingCardDetailsButton} semiboldXs`}>
                                    Details
                                </button>
                            </Link>
                        )}
                        {!bookingId && activeConversation?.studio?.id && (
                            <Link to={`/studio/${activeConversation.studio.id}`}>
                                <button className={`${classes.bookingCardDetailsButton} regularXs`}>
                                    Details
                                </button>
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className={classes.loadingContainer}>
                <header className={classes.loadingheader}>
                    <div className={classes.loadingProfileContainer}>
                        <div className={classes.profileContainer}>
                            <div className={classes.profileShadowImageContainer}>
                            </div>
                        </div>
                        <div className={classes.loadingName}></div>
                    </div>
                </header>
                <div className={classes.loadingDots}>
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                </div>
                <form onSubmit={handleSendMessage} className={classes.inputLoadingContainer}>
                    <textarea
                        ref={textareaRef}
                        placeholder="Nachricht schreiben"
                        className={`${classes.messageInput} regularMiddle`}
                        rows="1"
                        style={{
                            resize: "none",
                            minHeight: "24px",
                            maxHeight: "115px",
                            overflowY: "auto"
                        }}
                        disabled={sendingMessage}
                    />
                    <button 
                        type="submit" 
                        className={`${classes.sendButton} mediumLarge`}
                        disabled={!newMessage.trim() || sendingMessage}
                    >
                        Senden
                    </button>
                </form>
            </div>
        );
    }

    if (!activeConversation && isLoading) {
        return (
            <div className={classes.loadingContainer}>
                <header className={classes.loadingheader}>
                    <div className={classes.loadingProfileContainer}>
                        <div className={classes.profileContainer}>
                            <div className={classes.profileShadowImageContainer}>
                            </div>
                        </div>
                        <div className={classes.loadingName}></div>
                    </div>
                </header>
                <div className={classes.loadingDots}>
                    <span>.</span>
                    <span>.</span>
                    <span>.</span>
                </div>
            </div>
        );
    }

    if (!activeConversation) {
        return (
                <div className={classes.noConversation}>
                    Wähle eine Unterhaltung aus, um mit dem Messaging zu beginnen
                </div>
        );
    }

    // Get the chat partner information
    const chatPartner = activeConversation.chatPartner || { 
        firstName: 'Unbekannter Nutzer',
        profilePicture: '' 
    };

  // Ensure we also fall back for empty strings, not just null/undefined
  const getSafeImageSrc = (value) => {
      if (typeof value !== 'string') return fakeProfile;
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : fakeProfile;
  };
  const chatPartnerImageSrc = showSupportBranding
      ? SUPPORT_LOGO_SRC
      : getSafeImageSrc(chatPartner?.profilePicture);
  const chatPartnerName = showSupportBranding ? 'Stu Support' : chatPartner.firstName;
  const profileHeaderContent = (
      <>
          <div className={classes.profileImageContainer}>
              <img className={classes.profileImage} src={chatPartnerImageSrc} alt={chatPartnerName} />
          </div>
          <div className={classes.profileNameContainer}>
              <h1 className="semiboldMiddle">{chatPartnerName}</h1>
          </div>
      </>
  );

  return (
    <div className={`${classes.container} ${(isSupportChat || !isStudioWindowOpen) ? classes.expanded : classes.collapsed} ${displayMessagesPhone ? classes.displayMessagesPhone : ''}`}>
        <header className={classes.header}>
            {isSupportChat ? (
                <div className={classes.profileContainer}>{profileHeaderContent}</div>
            ) : (
                <Link to={`/users/show/${activeConversation?.chatPartner?.id}`} className={classes.profileContainer}>
                    {profileHeaderContent}
                </Link>
            )}
            {!isStudioWindowOpen && !isSupportChat && (
                <button className={`${classes.openButton} semiboldXs`} onClick={() => setIsStudioWindowOpen(true)}>
                    Details anzeigen
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
                    <path fillRule="evenodd" clipRule="evenodd" d="M6.65685 0.343262L7.36396 1.05037L2.41421 6.00012L7.36396 10.9499L6.65685 11.657L1 6.00012L6.65685 0.343262Z" fill="#77808A"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M10.6569 6.53857H1.65685V5.53857H10.6569V6.53857Z" fill="#77808A"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M6.65685 0.343262L7.36396 1.05037L2.41421 6.00012L7.36396 10.9499L6.65685 11.657L1 6.00012L6.65685 0.343262Z" stroke="#77808A" strokeWidth="0.4"/>
                    <path fillRule="evenodd" clipRule="evenodd" d="M10.6569 6.53857H1.65685V5.53857H10.6569V6.53857Z" stroke="#77808A" strokeWidth="0.4"/>
                    </svg>
                </button>
            )}
        </header>
        <div className={classes.headerPhone}>
            <div className={classes.headerPhoneContainer}>
                <button className={classes.backButton} onClick={() => {
                    // Clear the phone active conversation and hide the messages view
                    setPhoneActiveConversation(null);
                    setDisplayMessagesPhone(false);
                    
                    // Navigate to the main messages page
                    navigate('/messages', { replace: true });
                }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="21" viewBox="0 0 12 21" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M11.5 19.5727L9.96996 21L0 10.0808L9.96996 0L11.4408 1.48717L2.8824 10.1407L11.5 19.5727Z" fill="#101010"/>
                    </svg>
                </button>
                <div className={classes.profileImageContainer}>
                    <img className={classes.profileImage} src={chatPartnerImageSrc} alt={chatPartnerName} />
                </div>
                <div className={classes.profileNameContainer}>
                    <h1 className="semiboldMiddle">{chatPartnerName}</h1>
                    <p className="mediumMini">
                        {isSupportChat ? 'Direkter Support-Chat' : (
                          <>
                            {activeConversation.studio?.StudioName || activeConversation.name || 'Unbekanntes Studio'} ·
                            {activeConversation.lastMessageAt ?
                            new Date(activeConversation.lastMessageAt).toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: '2-digit'
                            }) : ''}
                          </>
                        )}
                    </p>
                </div>
            </div>
            {activeConversation.studio && (
                <Link to={`/studio/${activeConversation.studio.id}`}>
                    <div className={classes.bookingButton}>
                        <button className={`${classes.openButton} semiboldXs`}>
                            Buchung
                        </button>
                    </div>
                </Link>
            )}
        </div>
        <div
            ref={messagesScrollRef}
            className={classes.messagesContainer}
            onScroll={handleMessagesScroll}
        >
            {shouldShowMessagesLoading ? (
                <div className={classes.messagesLoadingContainer}>
                    <div className={classes.loadingSpinner}></div>
                </div>
            ) : (
                <>
            {loadingOlder && (
                <div className={classes.loadOlderBanner} aria-live="polite">
                    <span className={`${classes.loadOlderText} regularXxs`}>Ältere Nachrichten werden geladen…</span>
                </div>
            )}
            {(() => {
                // Don't show booking card for inquiry-only chats (no actual booking)
                const isInquiryOnly = activeConversation?.chatType === 'INQUIRY' && 
                                     !exactLinkedBookingId &&
                                     !booking;
                
                if (isInquiryOnly) {
                    // Find the Erkundigung message (first non-system message)
                    const findErkundigungMessage = () => {
                        if (!allMessages || allMessages.length === 0) return null;
                        
                        // For inquiry-only chats, find the first non-system message
                        for (let i = 0; i < allMessages.length; i++) {
                            const msg = allMessages[i];
                            // Skip system messages and booking status messages
                            const isSystemMsg = msg.isSystemMessage || (msg.type && isBookingStatusMessage(msg.type));
                            if (!isSystemMsg && msg.content && msg.content.trim() !== '') {
                                return msg;
                            }
                        }
                        
                        return null;
                    };
                    
                    const erkundigungMessage = findErkundigungMessage();
                    
                    // Render Erkundigung message as a regular user message
                    const renderErkundigungMessage = () => {
                        if (!erkundigungMessage) return null;
                        
                        const displayContent = cleanMessageContent(erkundigungMessage.content || '');
                        if (!displayContent || displayContent.trim() === '') return null;
                        
                        // Determine if message is sent by current user (right side) or received from other user (left side)
                        const isSentByCurrentUser = erkundigungMessage.senderID === userId;
                        const messageIndex = allMessages.findIndex(msg => msg.id === erkundigungMessage.id);
                        
                        return (
                            <div 
                                key={`erkundigung-${erkundigungMessage.id}`}
                                className={`${classes.messageWrapper} ${
                                    isSentByCurrentUser ? classes.sent : classes.received
                                } ${
                                    isNextMessageFromSameUser(messageIndex, allMessages) ? classes.sameUserGap : classes.differentUserGap
                                }`}
                            >
                                <div className={classes.userImageContainer}>
                                    {shouldShowProfileImage(messageIndex, allMessages) && (
                                        <img 
                                            src={isSentByCurrentUser 
                                                ? getSafeImageSrc(profilePicture)
                                                : chatPartnerImageSrc
                                            } 
                                            alt="user" 
                                        />
                                    )}
                                </div>
                                <div className={classes.message}>
                                    <p>{displayContent}</p>
                                    <span className={`${classes.timestamp} semiboldXxs`}>
                                        {formatMessageTime(erkundigungMessage.createdAt)}
                                    </span>
                                </div>
                            </div>
                        );
                    };
                    
                    // Get first message date for inquiry date separator
                    const firstMessageDate = allMessages && allMessages.length > 0 ? allMessages[0]?.createdAt : null;
                    return (
                        <>
                            {/* Show conversation start date if available */}
                            {activeConversation?.createdAt && (
                                <div className={classes.conversationStartDate}>
                                    <span className="regularXxs">
                                        {formatConversationStartDate(activeConversation.createdAt)}
                                    </span>
                                </div>
                            )}
                            {/* Show top status message for inquiry chats */}
                            {renderTopStatusMessage()}
                            {/* Show inquiry date separator between status and messages */}
                            {firstMessageDate && (
                                <div className={classes.conversationStartDate}>
                                    <span className="regularXxs">
                                        {formatInquiryDate(firstMessageDate)}
                                    </span>
                                </div>
                            )}
                            {/* Show Erkundigung message for inquiry-only chats (after date separator) */}
                            {erkundigungMessage && renderErkundigungMessage()}
                        </>
                    );
                }
                
                const hasBooking = !!booking;
                const shouldShowCard = hasBooking;
                
                // Get first message date for inquiry date separator (if inquiry chat)
                const isInquiryChat = activeConversation?.chatType === 'INQUIRY';
                const firstMessageDate = isInquiryChat && allMessages && allMessages.length > 0 ? allMessages[0]?.createdAt : null;
                
                // Find the Erkundigung message (first non-system message before any BOOKING_CREATED)
                // This is the inquiry message that was sent before the booking was created
                const findErkundigungMessage = () => {
                    if (!allMessages || allMessages.length === 0) return null;
                    
                    // Find the first BOOKING_CREATED message to know where booking started
                    const firstBookingCreatedIndex = allMessages.findIndex(msg => msg.type === 'BOOKING_CREATED');
                    
                    // If there's a booking, find the first non-system message before it
                    // If no booking yet, find the first non-system message
                    const searchLimit = firstBookingCreatedIndex >= 0 ? firstBookingCreatedIndex : allMessages.length;
                    
                    for (let i = 0; i < searchLimit; i++) {
                        const msg = allMessages[i];
                        // Skip system messages and booking status messages
                        const isSystemMsg = msg.isSystemMessage || (msg.type && isBookingStatusMessage(msg.type));
                        if (!isSystemMsg && msg.content && msg.content.trim() !== '') {
                            return msg;
                        }
                    }
                    
                    return null;
                };
                
                const erkundigungMessage = findErkundigungMessage();
                
                // Render Erkundigung message as a regular user message
                const renderErkundigungMessage = () => {
                    if (!erkundigungMessage) return null;
                    
                    const displayContent = cleanMessageContent(erkundigungMessage.content || '');
                    if (!displayContent || displayContent.trim() === '') return null;
                    
                        // Determine if message is sent by current user (right side) or received from other user (left side)
                        const isSentByCurrentUser = erkundigungMessage.senderID === userId;
                        const messageIndex = allMessages.findIndex(msg => msg.id === erkundigungMessage.id);
                    
                    return (
                        <div 
                            key={`erkundigung-${erkundigungMessage.id}`}
                            className={`${classes.messageWrapper} ${
                                isSentByCurrentUser ? classes.sent : classes.received
                            } ${
                                isNextMessageFromSameUser(messageIndex, allMessages) ? classes.sameUserGap : classes.differentUserGap
                            }`}
                        >
                                <div className={classes.userImageContainer}>
                                    {shouldShowProfileImage(messageIndex, allMessages) && (
                                    <img 
                                        src={isSentByCurrentUser 
                                            ? getSafeImageSrc(profilePicture)
                                            : chatPartnerImageSrc
                                        } 
                                        alt="user" 
                                    />
                                )}
                            </div>
                            <div className={classes.message}>
                                <p>{displayContent}</p>
                                <span className={`${classes.timestamp} semiboldXxs`}>
                                    {formatMessageTime(erkundigungMessage.createdAt)}
                                </span>
                            </div>
                        </div>
                    );
                };
                
                return (
                    <>
                        {/* Show conversation start date if available */}
                        {activeConversation?.createdAt && !isSupportChat && (
                            <div className={classes.conversationStartDate}>
                                <span className="regularXxs">
                                    {formatConversationStartDate(activeConversation.createdAt)}
                                </span>
                            </div>
                        )}
                        {/* Show top status message if booking-related */}
                        {shouldShowCard && renderTopStatusMessage()}
                        {/* Show Erkundigung message above booking card */}
                        {shouldShowCard && erkundigungMessage && renderErkundigungMessage()}
                        {/* Show booking card if booking exists, conversation is booking-related, or has a studio */}
                        {shouldShowCard && (() => {
                            const card = renderBookingCard();
                            return card;
                        })()}
                        {/* Show inquiry date separator between status/card and messages */}
                        {isInquiryChat && firstMessageDate && (
                            <div className={classes.conversationStartDate}>
                                <span className="regularXxs">
                                    {formatInquiryDate(firstMessageDate)}
                                </span>
                            </div>
                        )}
                    </>
                );
            })()}
            
            {(() => {
                if (messagesLoading) {
                    return null; // Will show loading state
                }
                
                if ((!messages || messages.length === 0) && optimisticMessages.length === 0) {
                    return (
                        <div className={classes.noMessages}>
                            
                        </div>
                    );
                }
                
                // Only skip the erkundigung when it was actually rendered above (booking card or inquiry header).
                // For support chats and non-booking/non-inquiry chats there is nothing rendered above,
                // so skipping the first message here would make it permanently invisible.
                const wasErkundigungShownAbove = !!booking || activeConversation?.chatType === 'INQUIRY';
                const findErkundigungMessageForSkip = () => {
                    if (!wasErkundigungShownAbove) return null;
                    if (!allMessages || allMessages.length === 0) return null;
                    const firstBookingCreatedIndex = allMessages.findIndex(msg => msg.type === 'BOOKING_CREATED');
                    const searchLimit = firstBookingCreatedIndex >= 0 ? firstBookingCreatedIndex : allMessages.length;
                    for (let i = 0; i < searchLimit; i++) {
                        const msg = allMessages[i];
                        const isSystemMsg = msg.isSystemMessage || (msg.type && isBookingStatusMessage(msg.type));
                        if (!isSystemMsg && msg.content && msg.content.trim() !== '') {
                            return msg;
                        }
                    }
                    return null;
                };
                const erkundigungMessageForSkip = findErkundigungMessageForSkip();
                const erkundigungMessageId = erkundigungMessageForSkip?.id;
                
                return allMessages.map((message, index) => {
                    // Skip Erkundigung message if it's already displayed above booking card
                    if (erkundigungMessageId && message.id === erkundigungMessageId) {
                        return null;
                    }
                    
                    // Check if this is a booking status message (system message)
                    const isBookingStatus = message.type && isBookingStatusMessage(message.type);
                    const isSystemMessage = message.isSystemMessage || isBookingStatus;
                    const isBookingCreated = message.type === 'BOOKING_CREATED';
                    
                    // Use booking from query if available, otherwise use message's relatedBookingID to fetch separately
                    // For now, use the main booking data - we can enhance this later to fetch per-message booking data
                    const bookingData = booking;
                    
                    // Handle BOOKING_CREATED specially:
                    // - Don't render as system message (booking card shows at top)
                    // - But if it has content (project description), render as regular user message
                    // - ALWAYS display as sent by Entdecker (guest) since they created the booking
                    if (isBookingCreated) {
                        // Try to get project description from booking object first, then from message content
                        let displayContent = '';
                        
                        // Priority 1: Get from booking object
                        if (bookingData?.projectDescription) {
                            displayContent = bookingData.projectDescription;
                        } else if (bookingData?.description) {
                            displayContent = bookingData.description;
                        } else if (message.content) {
                            // Priority 2: Clean message content - remove booking info
                            displayContent = cleanMessageContent(message.content);
                        }
                        
                        // Determine if this message should be displayed as sent by Entdecker
                        // Booking is always created by the guest (Entdecker)
                        const guestUserId = bookingData?.userID || bookingData?.guestID || activeConversation?.guestID;
                        const isSentByEntdecker = guestUserId === userId;
                        
                        // If there's content after cleaning, render as regular user message
                        // Always display as sent by Entdecker (guest who created the booking)
                        if (displayContent && displayContent.trim() !== '') {
                            return (
                                <div className={classes.messageContainer} key={message.id}>
                                    {renderDateSeparator(message, index, allMessages)}
                                    <div 
                                        className={`${classes.messageWrapper} ${
                                            isSentByEntdecker ? classes.sent : classes.received
                                        } ${
                                            isNextMessageFromSameUser(index, allMessages) ? classes.sameUserGap : classes.differentUserGap
                                        }`}
                                    >
                                        <div className={classes.userImageContainer}>
                                            {shouldShowProfileImage(index, allMessages) && (
                                                <img 
                                                    src={isSentByEntdecker 
                                                        ? getSafeImageSrc(profilePicture)
                                                        : chatPartnerImageSrc
                                                    } 
                                                    alt="user" 
                                                />
                                            )}
                                        </div>
                                        <div className={classes.message}>
                                            <p>{displayContent}</p>
                                            <span className={`${classes.timestamp} semiboldXxs`}>
                                                {formatMessageTime(message.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                        // If no content, skip it (booking card shows at top)
                        return null;
                    }
                    
                    // ALWAYS render other booking status messages as centered system messages
                    // System messages are displayed centered, not as user messages
                    if (isBookingStatus) {
                        const statusMessage = renderBookingStatusMessage(message, bookingData);
                        if (statusMessage) {
                            return (
                                <div className={classes.messageContainer} key={message.id}>
                                    {renderDateSeparator(message, index, allMessages)}
                                    <div className={classes.systemMessageWrapper}>
                                        {statusMessage}
                                    </div>
                                </div>
                            );
                        }
                        // If status message couldn't be rendered, skip it
                        return null;
                    }
                    
                    // Skip system messages without content (they're handled above)
                    if (isSystemMessage && (!message.content || message.content.trim() === '')) {
                        return null;
                    }
                    
                    // Clean message content - remove booking info from all messages
                    // This ensures only the actual message text is shown, not booking details
                    const displayContent = cleanMessageContent(message.content || '');
                    
                    // Skip if content is empty after cleaning
                    if (!displayContent || displayContent.trim() === '') {
                        return null;
                    }
                    
                    const isOwnMessage = message.senderID === userId;
                    const isFailedSend = Boolean(message.sendFailed && isOwnMessage);

                    return (
                        <div className={classes.messageContainer} key={message.id}>
                            {renderDateSeparator(message, index, allMessages)}
                            <div 
                                className={`${classes.messageWrapper} ${
                                    isOwnMessage ? classes.sent : classes.received
                                        } ${
                                            isNextMessageFromSameUser(index, allMessages) ? classes.sameUserGap : classes.differentUserGap
                                        } ${isFailedSend ? classes.failedSendWrapper : ''}`}
                            >
                                {isFailedSend && (
                                    <div className={classes.failedSendActions}>
                                        <button
                                            type="button"
                                            className={classes.failedRetryButton}
                                            onClick={() => retryFailedMessage(message)}
                                            disabled={message.isSending || sendingMessage}
                                            aria-label="Nachricht erneut senden"
                                            title="Erneut senden"
                                        >
                                            <span className={classes.failedRetryIcon} aria-hidden>!</span>
                                        </button>
                                        <button
                                            type="button"
                                            className={classes.failedDismissButton}
                                            onClick={() => dismissFailedMessage(message.id)}
                                            aria-label="Nicht gesendete Nachricht entfernen"
                                            title="Entfernen"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 12 12" fill="none" aria-hidden>
                                                <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                <div className={classes.userImageContainer}>
                                    {shouldShowProfileImage(index, allMessages) && (
                                        <img 
                                            src={isOwnMessage
                                                ? getSafeImageSrc(profilePicture)
                                                : chatPartnerImageSrc
                                            } 
                                            alt="user" 
                                        />
                                    )}
                                </div>
                                <div className={`${classes.message} ${isFailedSend ? classes.messageFailed : ''} ${message.isSending ? classes.messageSending : ''}`}>
                                    <p>{displayContent}</p>
                                    <span className={`${classes.timestamp} semiboldXxs`}>
                                        {isFailedSend
                                            ? 'Nicht gesendet'
                                            : formatMessageTime(message.createdAt)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    );
                }).filter(Boolean) // Remove null entries
            })()}
            <div ref={messagesEndRef} />
                </>
            )}
        </div>
        <form onSubmit={handleSendMessage} className={classes.inputContainer}>
            <textarea
                ref={textareaRef}
                value={newMessage}
                onChange={handleMessageChange}
                onKeyDown={handleKeyDown}
                placeholder={
                    activeConversation?.chatStatus === 'READ_ONLY' && activeConversation?.chatType === 'INQUIRY'
                        ? "Diese Anfrage ist abgeschlossen"
                        : "Nachricht schreiben"
                }
                className={`${classes.messageInput} regularMiddle`}
                rows="1"
                style={{
                    resize: "none",
                    minHeight: "24px",
                    maxHeight: "115px",
                    overflowY: "auto"
                }}
                disabled={
                    activeConversation?.chatStatus === 'READ_ONLY' && activeConversation?.chatType === 'INQUIRY'
                }
            />
            <button 
                type="submit" 
                className={`${classes.sendButton} mediumLarge`}
                onMouseDown={(e) => e.preventDefault()}
                disabled={
                    !newMessage.trim() || 
                    sendingMessage || 
                    (activeConversation?.chatStatus === 'READ_ONLY' && activeConversation?.chatType === 'INQUIRY')
                }
            >
                Senden
            </button>
        </form>
    </div>
  );
};

export default Messages;
